import React, { useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  Container,
  Divider,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  Switch,
  FormControlLabel,
  Typography,
  Alert,
  Paper,
  CircularProgress,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import logging from "utils/logging";
import Provider from "provider";
import utils from "provider/firebase/firebaseUtils";
import {
  selectGameId,
  selectGameObject,
  selectGameStatusObject,
  selectFinalRevealState,
} from "features/gamestate/gameStateSlice";
import { selectAllPlayers } from "features/player/playerSlice";
import { selectCurrentQuestionObject } from "features/question/questionSlice";
import { advanceState, advanceFinalRevealStep } from "features/gamestate/GameState";
// Constants import not needed — we use getCallableFunction with string name directly
import JSONView from "./JSONView";

const log = logging.getLogger("DebugFinalReveal");

// Test scenario definitions (duplicated client-side for the picker UI)
const SCENARIOS = [
  { id: "happy-path-3p", name: "Happy Path (3 players, mixed)" },
  { id: "all-correct-3p", name: "All Correct (3 players)" },
  { id: "all-wrong-3p", name: "All Wrong (3 players)" },
  { id: "only-last-correct-3p", name: "Only Last Correct (3 players)" },
  { id: "runaway-3p", name: "Runaway Winner (leader > 2x second)" },
  { id: "comeback-3p", name: "Comeback Win (last place wins)" },
  { id: "exact-tie-3p", name: "Exact Tie (two players tied)" },
  { id: "wipeout-3p", name: "Player Goes to $0" },
  { id: "minimum-2p", name: "Two Players" },
  { id: "large-group-15p", name: "Large Group (15 players)" },
];

// Submission fixtures per scenario (for automation buttons)
const SUBMISSION_DATA = {
  "happy-path-3p": [
    { playerIndex: 0, wager: 5000, answer: "penicillin", isCorrect: true },
    { playerIndex: 1, wager: 4000, answer: "aspirin", isCorrect: false },
    { playerIndex: 2, wager: 0, answer: null, isCorrect: false },
  ],
  "all-correct-3p": [
    { playerIndex: 0, wager: 3000, answer: "Milton Friedman", isCorrect: true },
    { playerIndex: 1, wager: 5000, answer: "Friedman", isCorrect: true },
    { playerIndex: 2, wager: 4400, answer: "Milton Friedman", isCorrect: true },
  ],
  "all-wrong-3p": [
    { playerIndex: 0, wager: 4000, answer: "Al Capone", isCorrect: false },
    { playerIndex: 1, wager: 3000, answer: "John Dillinger", isCorrect: false },
    { playerIndex: 2, wager: 5200, answer: "Lucky Luciano", isCorrect: false },
  ],
  "only-last-correct-3p": [
    { playerIndex: 0, wager: 3000, answer: "Tolstoy", isCorrect: false },
    { playerIndex: 1, wager: 5000, answer: "Dostoevsky", isCorrect: false },
    { playerIndex: 2, wager: 2000, answer: "Chekhov", isCorrect: true },
  ],
  "runaway-3p": [
    { playerIndex: 0, wager: 3200, answer: "Lowes", isCorrect: false },
    { playerIndex: 1, wager: 5400, answer: "Home Depot", isCorrect: true },
    { playerIndex: 2, wager: 100, answer: "Home Depot", isCorrect: true },
  ],
  "comeback-3p": [
    { playerIndex: 0, wager: 3800, answer: "Navajo", isCorrect: true },
    { playerIndex: 1, wager: 6000, answer: "Apache", isCorrect: false },
    { playerIndex: 2, wager: 5000, answer: "Cherokee", isCorrect: false },
  ],
  "exact-tie-3p": [
    { playerIndex: 0, wager: 4000, answer: "Griffith Observatory", isCorrect: true },
    { playerIndex: 1, wager: 2000, answer: "the Hollywood Sign", isCorrect: false },
    { playerIndex: 2, wager: 3000, answer: "Griffith Observatory", isCorrect: true },
  ],
  "wipeout-3p": [
    { playerIndex: 0, wager: 4200, answer: "Ionesco", isCorrect: false },
    { playerIndex: 1, wager: 2000, answer: "Samuel Beckett", isCorrect: true },
    { playerIndex: 2, wager: 6000, answer: "Pinter", isCorrect: false },
  ],
  "minimum-2p": [
    { playerIndex: 0, wager: 5600, answer: "North Carolina", isCorrect: true },
    { playerIndex: 1, wager: 3000, answer: "Virginia", isCorrect: false },
  ],
  "large-group-15p": [
    { playerIndex: 0, wager: 2200, answer: null, isCorrect: false },
    { playerIndex: 1, wager: 1000, answer: "Stay Cool With Cal", isCorrect: false },
    { playerIndex: 2, wager: 3800, answer: "Keep Cool With Coolidge", isCorrect: true },
    { playerIndex: 3, wager: 2000, answer: "A Return to Normalcy", isCorrect: false },
    { playerIndex: 4, wager: 4600, answer: null, isCorrect: false },
    { playerIndex: 5, wager: 3000, answer: "Keep Cool With Coolidge", isCorrect: true },
    { playerIndex: 6, wager: 1000, answer: "Tippecanoe and Tyler Too", isCorrect: false },
    { playerIndex: 7, wager: 5800, answer: "Keep Cool With Coolidge", isCorrect: true },
    { playerIndex: 8, wager: 2000, answer: "I Like Ike", isCorrect: false },
    { playerIndex: 9, wager: 6600, answer: null, isCorrect: false },
    { playerIndex: 10, wager: 4000, answer: "Keep Cool With Coolidge", isCorrect: true },
    { playerIndex: 11, wager: 3000, answer: "Morning in America", isCorrect: false },
    { playerIndex: 12, wager: 7800, answer: "Keep Cool With Coolidge", isCorrect: true },
    { playerIndex: 13, wager: 2000, answer: "Happy Days Are Here Again", isCorrect: false },
    { playerIndex: 14, wager: 1000, answer: "Keep Cool With Coolidge", isCorrect: true },
  ],
};

// ── Scenario Picker ──────────────────────────────────────────────────────────

const ScenarioPicker = () => {
  const [scenarioId, setScenarioId] = useState("");
  const [seeding, setSeeding] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const handleSeed = useCallback(async () => {
    if (!scenarioId) return;
    setSeeding(true);
    setError(null);
    try {
      const seedFn = utils.getCallableFunction("seedFinalTestScenarioCallable");
      const result = await seedFn({ scenarioId });
      const { gameId } = result.data;
      log.info("Seeded scenario", { scenarioId, gameId });
      navigate(`/manage/game/${gameId}/debug-final`);
    } catch (err) {
      log.error("Seed failed", err);
      setError(err.message);
    } finally {
      setSeeding(false);
    }
  }, [scenarioId, navigate]);

  return (
    <Container maxWidth="sm" sx={{ mt: 4 }}>
      <Typography variant="h5" gutterBottom>
        Debug Final Reveal — Scenario Picker
      </Typography>
      <Stack spacing={2}>
        <FormControl fullWidth>
          <InputLabel>Scenario</InputLabel>
          <Select
            value={scenarioId}
            label="Scenario"
            onChange={(e) => setScenarioId(e.target.value)}
          >
            {SCENARIOS.map((s) => (
              <MenuItem key={s.id} value={s.id}>
                {s.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="contained"
          onClick={handleSeed}
          disabled={!scenarioId || seeding}
          startIcon={seeding ? <CircularProgress size={16} /> : <PlayArrowIcon />}
        >
          {seeding ? "Seeding..." : "Seed & Open"}
        </Button>
        {error && <Alert severity="error">{error}</Alert>}
      </Stack>
    </Container>
  );
};

// ── Automation Buttons ───────────────────────────────────────────────────────

const PhaseButtons = ({ gameObject, players, questionId, scenarioId }) => {
  const dispatch = useDispatch();
  const [running, setRunning] = useState(null);
  const [error, setError] = useState(null);
  const currentQuestion = useSelector(selectCurrentQuestionObject);

  const sortedPlayers = [...players].sort((a, b) => {
    const nameA = a.name?.toLowerCase() || "";
    const nameB = b.name?.toLowerCase() || "";
    return nameA.localeCompare(nameB);
  });

  const submissions = scenarioId ? SUBMISSION_DATA[scenarioId] : null;

  const runPhase = useCallback(
    async (phase, fn) => {
      setRunning(phase);
      setError(null);
      try {
        await fn();
      } catch (err) {
        log.error(`Phase ${phase} failed`, err);
        setError(`${phase}: ${err.message}`);
      } finally {
        setRunning(null);
      }
    },
    []
  );

  const handleAdvanceState = useCallback(() => {
    runPhase("advance", () => advanceState(gameObject, players, dispatch, currentQuestion));
  }, [runPhase, gameObject, players, dispatch, currentQuestion]);

  const handleAdvanceRevealStep = useCallback(() => {
    runPhase("reveal-step", () => advanceFinalRevealStep(gameObject, dispatch));
  }, [runPhase, gameObject, dispatch]);

  const handleSubmitAllWagers = useCallback(async () => {
    if (!submissions || !questionId) return;
    await runPhase("wagers", async () => {
      for (const sub of submissions) {
        const playerId = sortedPlayers[sub.playerIndex]?.id;
        if (!playerId) continue;
        log.info(`Submitting wager for ${sortedPlayers[sub.playerIndex].name}: $${sub.wager}`);
        await Provider.submitWager(gameObject.id, questionId, playerId, sub.wager);
      }
      log.info("All wagers submitted");
    });
  }, [submissions, questionId, sortedPlayers, gameObject, runPhase]);

  const handleSubmitAllAnswers = useCallback(async () => {
    if (!submissions || !questionId) return;
    await runPhase("answers", async () => {
      for (const sub of submissions) {
        const playerId = sortedPlayers[sub.playerIndex]?.id;
        if (!playerId || sub.answer === null) continue;
        log.info(`Submitting answer for ${sortedPlayers[sub.playerIndex].name}: "${sub.answer}"`);
        await Provider.submitQuestionAction(gameObject.id, questionId, playerId, sub.answer);
      }
      log.info("All answers submitted");
    });
  }, [submissions, questionId, sortedPlayers, gameObject, runPhase]);

  const handleRunReveals = useCallback(async () => {
    await runPhase("reveals", async () => {
      // Click advance repeatedly with a delay between each
      for (let i = 0; i < 100; i++) {
        const currentState = gameObject.state?.finalReveal?.currentStep;
        if (currentState === "winner") break;
        if (gameObject.state?.roundState === "credits") break;
        await advanceFinalRevealStep(gameObject, dispatch);
        await new Promise((r) => setTimeout(r, 1500));
      }
    });
  }, [runPhase, gameObject, dispatch]);

  return (
    <Stack spacing={1}>
      <Typography variant="subtitle2" color="text.secondary">
        Phase Buttons
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Button
          variant="outlined"
          size="small"
          onClick={handleAdvanceState}
          disabled={!!running}
          startIcon={<SkipNextIcon />}
        >
          Advance State
        </Button>
        {submissions && (
          <>
            <Button
              variant="outlined"
              size="small"
              onClick={handleSubmitAllWagers}
              disabled={!!running}
            >
              {running === "wagers" ? "Submitting..." : "Submit All Wagers"}
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handleSubmitAllAnswers}
              disabled={!!running}
            >
              {running === "answers" ? "Submitting..." : "Submit All Answers"}
            </Button>
          </>
        )}
        <Button
          variant="outlined"
          size="small"
          onClick={handleRunReveals}
          disabled={!!running}
        >
          {running === "reveals" ? "Revealing..." : "Run Reveals (auto-step)"}
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={handleAdvanceRevealStep}
          disabled={!!running}
        >
          Advance Reveal Step
        </Button>
      </Stack>
      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}
    </Stack>
  );
};

const GranularButtons = ({ gameObject, players, questionId, scenarioId }) => {
  const [error, setError] = useState(null);
  const submissions = scenarioId ? SUBMISSION_DATA[scenarioId] : null;

  const sortedPlayers = [...players].sort((a, b) => {
    const nameA = a.name?.toLowerCase() || "";
    const nameB = b.name?.toLowerCase() || "";
    return nameA.localeCompare(nameB);
  });

  if (!submissions) {
    return (
      <Alert severity="info">
        No submission data — game was not seeded from a known scenario.
      </Alert>
    );
  }

  const handleSubmitWager = async (sub) => {
    setError(null);
    try {
      const playerId = sortedPlayers[sub.playerIndex]?.id;
      if (!playerId || !questionId) return;
      await Provider.submitWager(gameObject.id, questionId, playerId, sub.wager);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSubmitAnswer = async (sub) => {
    setError(null);
    try {
      const playerId = sortedPlayers[sub.playerIndex]?.id;
      if (!playerId || !questionId || sub.answer === null) return;
      await Provider.submitQuestionAction(gameObject.id, questionId, playerId, sub.answer);
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <Stack spacing={1}>
      <Typography variant="subtitle2" color="text.secondary">
        Per-Player Actions
      </Typography>
      {submissions.map((sub) => {
        const player = sortedPlayers[sub.playerIndex];
        if (!player) return null;
        return (
          <Stack key={sub.playerIndex} direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" sx={{ minWidth: 80 }}>
              {player.name}
            </Typography>
            <Button size="small" variant="text" onClick={() => handleSubmitWager(sub)}>
              Wager ${sub.wager.toLocaleString()}
            </Button>
            <Button
              size="small"
              variant="text"
              onClick={() => handleSubmitAnswer(sub)}
              disabled={sub.answer === null}
            >
              Answer: {sub.answer || "(none)"}
            </Button>
            <Chip
              size="small"
              label={sub.isCorrect ? "correct" : "incorrect"}
              color={sub.isCorrect ? "success" : "error"}
              variant="outlined"
            />
          </Stack>
        );
      })}
      {error && <Alert severity="error">{error}</Alert>}
    </Stack>
  );
};

// ── Main Component ───────────────────────────────────────────────────────────

const DebugFinalReveal = () => {
  const gameObject = useSelector(selectGameObject);
  const gameStateObj = useSelector(selectGameStatusObject);
  const gameId = useSelector(selectGameId);
  const players = useSelector(selectAllPlayers);
  const finalReveal = useSelector(selectFinalRevealState);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // If no game is loaded, show the scenario picker
  if (!gameId) {
    return <ScenarioPicker />;
  }

  const questionId = gameStateObj.questionId;
  const scenarioId = gameObject?.subtitle
    ?.replace("Scenario: ", "")
    ?.trim();

  return (
    <Container maxWidth="md" sx={{ py: 2 }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Typography variant="h5">Debug Final Reveal</Typography>
        <Chip
          label={`${gameStateObj.currentState}.${gameStateObj.round}.${gameStateObj.roundState}`}
          size="small"
          color="primary"
          variant="outlined"
        />
        {finalReveal?.currentStep && (
          <Chip
            label={`reveal: ${finalReveal.currentStep}`}
            size="small"
            color="secondary"
            variant="outlined"
          />
        )}
        <Box sx={{ flex: 1 }} />
        <Link
          href={`/gameboard/${gameId}`}
          target="_blank"
          rel="noopener"
          sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
        >
          Open Gameboard <OpenInNewIcon fontSize="small" />
        </Link>
      </Stack>

      {scenarioId && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Scenario: <strong>{scenarioId}</strong>
        </Alert>
      )}

      <Divider sx={{ mb: 2 }} />

      {/* Automation Buttons */}
      <PhaseButtons
        gameObject={gameObject}
        players={players}
        questionId={questionId}
        scenarioId={scenarioId}
      />

      <Divider sx={{ my: 2 }} />

      {/* Advanced toggle */}
      <FormControlLabel
        control={
          <Switch
            checked={showAdvanced}
            onChange={(e) => setShowAdvanced(e.target.checked)}
          />
        }
        label="Show granular controls"
      />

      {showAdvanced && (
        <Box sx={{ mt: 1 }}>
          <GranularButtons
            gameObject={gameObject}
            players={players}
            questionId={questionId}
            scenarioId={scenarioId}
          />
        </Box>
      )}

      <Divider sx={{ my: 2 }} />

      {/* State Display */}
      <Stack spacing={2}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Game State
          </Typography>
          <JSONView data={gameObject?.state} />
        </Paper>

        {finalReveal && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Final Reveal State
            </Typography>
            <JSONView data={finalReveal} />
          </Paper>
        )}

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Players ({players.length})
          </Typography>
          <JSONView
            data={players.map((p) => ({
              id: p.id,
              name: p.name,
              score: p.score,
              preFinalScore: p.preFinalScore,
              finalScore: p.finalScore,
            }))}
          />
        </Paper>
      </Stack>
    </Container>
  );
};

export default DebugFinalReveal;
